<template>
  <VaCard>
    <VaCardTitle class="separate-title">
      <h3 class="mb-3 font-bold text-lg" style="text-transform: none">
        Marcas por {{ selectedPeriod === 'Diario' ? 'día' : 'mes' }}
      </h3>

      <VaButtonToggle v-model="selectedPeriod" :options="periods" color="background-element" size="small" />
    </VaCardTitle>
    <VaCardContent class="content-container">
      <section class="flex gap-x-10">
        <div class="data-container-tripulantes">
          <div class="">
            Promedio {{ selectedPeriod === 'Diario' ? 'diario' : 'mensual' }} de marcas
            {{ selectedPeriod === 'Diario' ? '' : 'diarias' }}
            <div class="text-xl font-bold mb-3">
              {{ selectedPeriod === 'Diario' ? promedioDia.toFixed(2) : promedioMes.toFixed(2) }}
            </div>
          </div>
          <span class="text-secondary">
            <p class="text-danger">
              <span :class="changeClass">
                <template v-if="up">
                  <VaIcon name="arrow_upward" />
                </template>
                <template v-else>
                  <VaIcon name="arrow_downward" />
                </template>
                {{ selectedPeriod === 'Diario' ? changePercentageDia.toFixed(2) : changePercentageMes.toFixed(2) }}
              </span>
              <span class="text-secondary">
                del promedio
                {{ selectedPeriod === 'Diario' ? lastValueDia.toFixed(2) : lastValueMes.toFixed(2) }}
                del {{ selectedPeriod === 'Diario' ? 'mes' : 'año' }} pasado</span
              >
            </p>
          </span>
          <p class="text-xs text-danger mt-2 mb-2">
            <span class="text-secondary"> Calculado desde el </span>
            <span class="text-secondary font-bold">
              {{ selectedPeriod === 'Diario' ? toDayString(yearStartDate) : toDayString(monthStartDate) }}
            </span>
            <span class="text-secondary"> hasta el </span>
            <span class="text-secondary font-bold">
              {{ toDayString(currentDate) }}
            </span>
          </p>
        </div>
      </section>
      <div class="chart-wrapper mb-2">
        <VaChart v-if="selectedPeriod === 'Diario'" :data="chartDataDia" type="bar" :options="options" />
        <VaChart v-if="selectedPeriod === 'Mensual'" :data="chartDataMes" type="bar" :options="options" />
      </div>
    </VaCardContent>
  </VaCard>
</template>

<script setup lang="ts">
import { VaCard } from 'vuestic-ui'
import VaChart from '../../../components/va-charts/VaChart.vue'
import { getChartData, useChartData } from '../../../data/charts/composables/useChartData'
import { ChartOptions } from 'chart.js'
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { OperatedViaje, ViajeCancelado } from '../../../services/api-services/ViajesServices'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import dataHubServices from '../../../services/data-hub-services/DataHubServices'
import { viajesHubName, ViajesHubPipelines } from '../../../services/data-hub-services/ViajesHub'
import { TBarChartData, TLineChartData } from '../../../data/types'
import { IMarca, MarcasController } from '../../../services/api-services/MarcasServices'

const periods = ['Diario', 'Mensual'].map((period) => ({ label: period, value: period }))
const selectedPeriod = ref('Diario') as Ref<'Diario' | 'Mensual'>

const dayLabels: Ref<string[]> = ref([])
const dayEventos: Ref<{ dia: Date; texto: string; events: number }[]> = ref([])
const monthLabels: Ref<string[]> = ref([])
const monthEventos: Ref<{ mes: Date; texto: string; events: number }[]> = ref([])

const fullViajesList: Ref<OperatedViaje[]> = ref([])

const marcasController = new MarcasController()
const marcasList: Ref<Array<IMarca>> = ref([])

const up: Ref<boolean> = ref(true)

const changeClass = computed(() => {
  return up.value ? 'text-success' : 'text-red-600'
})

watch(selectedPeriod, () => {
  if (selectedPeriod.value === 'Diario') {
    if (promedioDia.value >= lastValueDia.value) {
      up.value = true
    } else {
      up.value = false
    }
  } else {
    if (promedioMes.value >= lastValueMes.value) {
      up.value = true
    } else {
      up.value = false
    }
  }
})

const showList: Ref<
  {
    viaje: OperatedViaje
    day: Date // Es el dia exacto del viaje
    month: Date // El primer dia del mes del viaje
  }[]
> = ref([])

const lineChartDataViajesDia: Ref<TBarChartData> = ref({
  labels: [],
  datasets: [
    {
      label: 'Marcas',
      borderColor: '#04ada8',
      backgroundColor: '#04ada8',
      data: [],
    },
  ],
})

const lineChartDataViajesMes: Ref<TBarChartData> = ref({
  labels: [],
  datasets: [
    {
      label: 'Marcas',
      borderColor: '#04ada8',
      backgroundColor: '#04ada8',
      data: [],
    },
  ],
})

const chartDataDia: Ref<TBarChartData> = computed(() => getChartData(lineChartDataViajesDia.value))
const chartDataMes: Ref<TBarChartData> = computed(() => getChartData(lineChartDataViajesMes.value))

const options: ChartOptions<'bar'> = {
  scales: {
    x: {
      display: true,
      grid: {
        display: false, // Disable X-axis grid lines ("net")
      },
    },
    y: {
      display: true,
      grid: {
        display: false, // Disable Y-axis grid lines ("net")
      },
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
    },
  },
  color: '#9966ff66',
}

const currentMes = new Date().getMonth()
const currentYear = new Date().getFullYear()
const currentDay = new Date().getDate()
const previousMes = currentMes === 0 ? 11 : currentMes - 1
const previousYear = currentMes === 0 ? currentYear - 1 : currentYear

const yearStartDate = new Date(currentYear, 0, 1)
const monthStartDate = new Date(currentYear, currentMes, 1)
const currentDate = new Date(currentYear, currentMes, currentDay)
// Formatear fecha a DD-MM-YYYY
const toDayString = (date: Date) => {
  const day = date.getDate()
  const fday = day < 10 ? `0${day}` : day
  const month = date.getMonth() + 1
  const fmonth = month < 10 ? `0${month}` : month
  const year = date.getFullYear()
  return `${fday}-${fmonth}-${year}`
}

const lastValueDia = computed(() => {
  const isPreviousMonth = (label: string) => {
    const [dayStr, monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return year.toString() === previousYear.toString() && month.toString() === previousMes.toString()
  }
  const data = lineChartDataViajesDia.value
  const filteredLabels = data.labels!.filter((label) => isPreviousMonth(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total / 30
})
const promedioDia = computed(() => {
  const isCurrentMonth = (label: string) => {
    const [dayStr, monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return year.toString() === currentYear.toString() && month.toString() === currentMes.toString()
  }
  const data = lineChartDataViajesDia.value
  const filteredLabels = data.labels!.filter((label) => isCurrentMonth(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return filteredLabels.length > 0 ? total / currentDay : 0
})
const currentDiaValue = computed(() => {
  const isCurrentDay = (label: string) => {
    const [dayStr, monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    const day = parseInt(dayStr, 10)
    return (
      year.toString() === currentYear.toString() &&
      month.toString() === currentMes.toString() &&
      day.toString() === currentDay.toString()
    )
  }
  const data = lineChartDataViajesDia.value
  const filteredLabels = data.labels!.filter((label) => isCurrentDay(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total
})
const changePercentageDia = computed(() => {
  const promedio = promedioDia.value
  const lastPromedio = lastValueDia.value
  if (promedio >= lastPromedio) {
    up.value = true
  } else {
    up.value = false
  }
  return Math.abs(lastPromedio - promedio)
})
const lastValueMes = computed(() => {
  const isPreviousYear = (label: string) => {
    const [monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    return year.toString() === (currentYear - 1).toString()
  }
  const data = lineChartDataViajesMes.value
  const filteredLabels = data.labels!.filter((label) => isPreviousYear(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total / 12
})
const promedioMes = computed(() => {
  // Filtrar viajes de este año y calcular promedio mensual por meses pasados
  const isCurrentYear = (label: string) => {
    const [monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    return year.toString() === currentYear.toString()
  }
  const data = lineChartDataViajesMes.value
  const filteredLabels = data.labels!.filter((label) => isCurrentYear(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return filteredLabels.length > 0 ? total / (currentMes + 1) : 0
})
const currentMesValue = computed(() => {
  // Filtrar viajes de este año y calcular promedio mensual por meses pasados
  const isCurrentMonth = (label: string) => {
    const [monthStr, yearStr] = label.split('-')
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return year.toString() === currentYear.toString() && month.toString() === currentMes.toString()
  }
  const data = lineChartDataViajesMes.value
  const filteredLabels = data.labels!.filter((label) => isCurrentMonth(label as string))
  const total = filteredLabels.reduce((acc, label) => {
    const index = data.labels!.indexOf(label)
    const value = data.datasets[0].data[index]
    return acc + (typeof value === 'number' ? value : 0)
  }, 0)
  return total
})
const changePercentageMes = computed(() => {
  const promedio = promedioMes.value
  const lastValue = lastValueMes.value
  if (promedio >= lastValue) {
    up.value = true
  } else {
    up.value = false
  }
  return Math.abs(lastValue - promedio)
})

onMounted(() => {
  dataHubServices.subscribeToHub(viajesHubName, {
    subscriberID: 'fullPasajerosPorDiaBarsAR_viajes',
    pipeline: ViajesHubPipelines.VIAJES_WITH_TRIPULANTES,
    callback: (data: OperatedViaje[]) => {
      fullViajesList.value = data
    },
  })
})

watch(fullViajesList, () => {
  const showListTemp: {
    viaje: OperatedViaje
    day: Date
    month: Date
  }[] = []
  fullViajesList.value.forEach((viaje) => {
    if (viaje.posicionamientosData.length > 0 && viaje.estado !== null && !ViajeCancelado(viaje)) {
      const viajeDate = new Date(viaje.fecha_viaje)
      const day = new Date(viajeDate.getFullYear(), viajeDate.getMonth(), viajeDate.getDate())
      day.setHours(0, 0, 0, 0)
      const month = new Date(viajeDate.getFullYear(), viajeDate.getMonth(), 1)
      month.setHours(0, 0, 0, 0)
      showListTemp.push({
        viaje: viaje,
        day: day,
        month: month,
      })
    }
  })
  showList.value = showListTemp
})

watch(showList, async () => {
  // Fecha de inicio, fecha mas antigua de los meses
  const fechaInicio = showList.value.reduce((oldest, item) => {
    return item.month < oldest ? item.month : oldest
  }, new Date())
  // Fecha de fin, fecha mas reciente de los dias
  const fechaFin = showList.value.reduce((newest, item) => {
    return item.day > newest ? new Date(item.day) : newest
  }, new Date(0))
  fechaFin.setHours(23, 59, 59, 999)

  await marcasController.loadData(fechaInicio, fechaFin, 1000 * 30) // Recargar cada 30 segundos mientras se tengan datos
  marcasList.value = marcasController.getMarcasData()

  loadShowList()
})

function loadShowList() {
  const daySplittedSet = new Set<{ dia: Date; cant: number }>()
  const monthSplittedSet = new Set<{ mes: Date; cant: number }>()
  showList.value.forEach((item, index, array) => {
    const dayEntry = Array.from(daySplittedSet).find((d) => d.dia.getTime() === item.day.getTime())
    const dayMarks = marcasList.value.filter((m) => {
      const marcaDate = new Date(m.record_time)
      return (
        marcaDate.getFullYear() === item.day.getFullYear() &&
        marcaDate.getMonth() === item.day.getMonth() &&
        marcaDate.getDate() === item.day.getDate()
      )
    })
    if (!dayEntry) {
      daySplittedSet.add({ dia: item.day, cant: dayMarks.length })
    }
    const monthEntry = Array.from(monthSplittedSet).find((m) => m.mes.getTime() === item.month.getTime())
    const monthMarks = marcasList.value.filter((m) => {
      const marcaDate = new Date(m.record_time)
      const dayWithTrip = array.some((a) => {
        return (
          marcaDate.getFullYear() === a.day.getFullYear() &&
          marcaDate.getMonth() === a.day.getMonth() &&
          marcaDate.getDate() === a.day.getDate()
        )
      })
      return (
        marcaDate.getFullYear() === item.month.getFullYear() &&
        marcaDate.getMonth() === item.month.getMonth() &&
        dayWithTrip
      )
    })
    if (!monthEntry) {
      monthSplittedSet.add({ mes: item.month, cant: monthMarks.length })
    }
  })

  // Si no hay datos para el dia o mes actual, agregarlo con 0 eventos
  const today = new Date()
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const todayDayEntry = Array.from(daySplittedSet).find((d) => d.dia.getTime() === todayDay.getTime())
  if (!todayDayEntry) {
    daySplittedSet.add({ dia: todayDay, cant: 0 })
  }
  const todayMonthEntry = Array.from(monthSplittedSet).find((m) => m.mes.getTime() === todayMonth.getTime())
  if (!todayMonthEntry) {
    monthSplittedSet.add({ mes: todayMonth, cant: 0 })
  }

  // Obtener ultimos 30 dias y 12 meses
  const daySetList = Array.from(daySplittedSet)
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .slice(-30)
  const monthSetList = Array.from(monthSplittedSet)
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .slice(-12)

  dayLabels.value = daySetList
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .map((d) => {
      const day = d.dia.getDate()
      const month = d.dia.getMonth() + 1
      const year = d.dia.getFullYear()
      return `${day}-${month}-${year}`
    })
  dayEventos.value = daySetList
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .map((d) => ({ dia: d.dia, texto: `${d.cant} viajes`, events: d.cant }))
  monthLabels.value = monthSetList
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .map((m) => {
      const month = m.mes.getMonth() + 1
      const year = m.mes.getFullYear()
      return `${month}-${year}`
    })
  monthEventos.value = monthSetList
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .map((m) => ({ mes: m.mes, texto: `${m.cant} viajes`, events: m.cant }))
}

watch(
  () => [dayLabels.value, dayEventos.value],
  () => {
    lineChartDataViajesDia.value = {
      labels: dayLabels.value,
      datasets: [
        {
          label: 'Marcas',
          borderColor: '#04ada8',
          backgroundColor: '#04ada8',
          data: dayEventos.value.map((e) => e.events),
        },
      ],
    }
  },
  { immediate: true },
)

watch(
  () => [monthLabels.value, monthEventos.value],
  () => {
    lineChartDataViajesMes.value = {
      labels: monthLabels.value,
      datasets: [
        {
          label: 'Marcas',
          borderColor: '#04ada8',
          backgroundColor: '#04ada8',
          data: monthEventos.value.map((e) => e.events),
        },
      ],
    }
  },
  { immediate: true },
)
</script>
<style scoped>
.va-card {
  /*max-height: 580px;*/
}

.separate-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.content-container {
  display: flex;
  flex-direction: column;
  height: calc(100% - 48px);
}

.chart-wrapper {
  position: relative;
  width: 100%;
  height: 220px;
  min-width: 0;
}
</style>
